import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Button, Grid, CircularProgress, Snackbar, Alert } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { RootState } from '../../../reducers';
import { gotoPreviousMenu } from '../../../store/assessment.slice';
import { resetCarePlanError } from '../../../store/carePlanUpdate.slice';
import { saveEngagementDraft } from '../../../store/documentEngagement.slice';
import { SummaryNote } from '../../../Models/DocumentEngagements/SummaryNote.model';
import { SummaryNoteTypeEnum } from '../../../Enum/SummaryNoteTypeEnum';
import { copyObject } from '../../../utils/common';

import AlertDialog from '../../AlertDialog/AlertDialog';
import { assessmentStatusEnum } from '../../../utils/assessments';
import { CarePlanEnum } from '../../../Enum/CarePlanEnum';

import CarePlan from '../../IntegratedCarePlan/CarePlan/CarePlan';

const CarePlans: React.FC = () => {
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { error, integratedCarePlan } = useSelector((state: RootState) => state.carePlanUpdate);

    const { engagementDraft, memberAssessmentHistory, isSaving } = useSelector((state: RootState) => state.documentEngagement);
    const { isLoading } = useSelector((state: RootState) => state.shared);
    const isBusy = isSaving || isLoading;
    const isReviewed = Boolean(engagementDraft.IsCarePlanReviewed);
    const isUpdate = isReviewed && (integratedCarePlan.Goals?.some((goal) => goal.IsSelected) || false);

    const handleOnConfirmToNext = (markComplete = false) => {
        let data = copyObject(engagementDraft);
        data.SummaryNotes =
            data.SummaryNotes?.filter(
                (s) =>
                    s.Type !== SummaryNoteTypeEnum.Goal &&
                    s.Type !== SummaryNoteTypeEnum.Plan &&
                    s.Type !== SummaryNoteTypeEnum.Strength &&
                    s.Type !== SummaryNoteTypeEnum.Barrier &&
                    s.Type !== SummaryNoteTypeEnum.Metric &&
                    s.Type !== SummaryNoteTypeEnum.NextStep &&
                    s.Type !== SummaryNoteTypeEnum.SchedulePlan
            ) || [];

        integratedCarePlan.Goals.filter((g) => g.IsSelected).forEach((goal) => {
            const summaryNote = {
                Id: goal.Id,
                CptCode: '',
                Zcode: '',
                Note: goal.Status === CarePlanEnum.Achieved ? `Achieved Goal : ${goal.Name}` : goal.Name,
                Type: SummaryNoteTypeEnum.Goal,
                IsConfirm: true,
                ReferenceIds: [],
                OrderBy: 2,
                DisplayName: 'Goal',
                CategoryGroupId: '',
            } as SummaryNote;
            data.SummaryNotes.push(summaryNote);
        });

        integratedCarePlan.Strengths.filter((g) => g.IsSelected).forEach((strength) => {
            const summaryNote = {
                Id: strength.Id,
                CptCode: '',
                Zcode: '',
                Note: strength.Name,
                Type: SummaryNoteTypeEnum.Strength,
                IsConfirm: true,
                ReferenceIds: [],
                OrderBy: 20,
                DisplayName: 'Strength',
                CategoryGroupId: '',
            } as SummaryNote;
            data.SummaryNotes.push(summaryNote);
        });

        integratedCarePlan.Plans.filter((g) => g.IsSelected).forEach((plan) => {
            const summaryNote = {
                Id: plan.Id,
                CptCode: '',
                Zcode: '',
                Note: plan.Status === CarePlanEnum.Achieved ? `Achieved Plan : ${plan.Name}` : plan.Name,
                Type: SummaryNoteTypeEnum.Plan,
                IsConfirm: true,
                ReferenceIds: [],
                OrderBy: 13,
                DisplayName: 'Plan',
                CategoryGroupId: '',
            } as SummaryNote;
            data.SummaryNotes.push(summaryNote);
        });

        integratedCarePlan.Barriers.filter((g) => g.IsSelected).forEach((barrier) => {
            const summaryNote = {
                Id: barrier.Id,
                CptCode: '',
                Zcode: '',
                Note: barrier.Name,
                Type: SummaryNoteTypeEnum.Barrier,
                IsConfirm: true,
                ReferenceIds: [],
                OrderBy: 14,
                DisplayName: 'Barrier',
                CategoryGroupId: '',
            } as SummaryNote;
            data.SummaryNotes.push(summaryNote);
        });

        integratedCarePlan.Metrics.filter((g) => g.IsSelected).forEach((plan) => {
            const summaryNote = {
                Id: plan.Id,
                CptCode: '',
                Zcode: '',
                Note: plan.Name,
                Type: SummaryNoteTypeEnum.Metric,
                IsConfirm: true,
                ReferenceIds: [],
                OrderBy: 15,
                DisplayName: 'Metric',
                CategoryGroupId: '',
            } as SummaryNote;
            data.SummaryNotes.push(summaryNote);
        });

        integratedCarePlan.NextSteps.filter((g) => g.IsSelected).forEach((nextStep) => {
            const summaryNote = {
                Id: nextStep.Id,
                CptCode: '',
                Zcode: '',
                Note: nextStep.Name,
                Type: SummaryNoteTypeEnum.NextStep,
                IsConfirm: true,
                ReferenceIds: [],
                OrderBy: 16,
                DisplayName: 'NextStep',
                CategoryGroupId: '',
            } as SummaryNote;
            data.SummaryNotes.push(summaryNote);
        });

        integratedCarePlan.SchedulePlans.filter((g) => g.IsSelected).forEach((schedulePlan) => {
            var dayDescription = '';
            if (schedulePlan.TimeIntervalType === 1) {
                if (schedulePlan.TimeIntervalIndays === 1) dayDescription = 'Day';
                if (schedulePlan.TimeIntervalIndays > 1) dayDescription = 'Days';
            } else if (schedulePlan.TimeIntervalType === 2) {
                if (schedulePlan.TimeIntervalIndays === 7) dayDescription = 'Week';
                if (schedulePlan.TimeIntervalIndays > 7) dayDescription = 'Weeks';
            } else if (schedulePlan.TimeIntervalType === 3) {
                if (schedulePlan.TimeIntervalIndays === 1) dayDescription = 'Month';
                if (schedulePlan.TimeIntervalIndays > 1) dayDescription = 'Months';
            }
            const schedulePlanDetail = `Appointment with ${schedulePlan.Provider.Role} ${schedulePlan.Provider.Name} occurs every ${schedulePlan.TimeIntervalIndays} ${dayDescription}`;
            const summaryNote = {
                Id: schedulePlan.Id,
                CptCode: '',
                Zcode: '',
                Note: schedulePlanDetail,
                Type: SummaryNoteTypeEnum.SchedulePlan,
                IsConfirm: true,
                ReferenceIds: [],
                OrderBy: 17,
                DisplayName: 'Schedule Plan',
                CategoryGroupId: '',
            } as SummaryNote;
            data.SummaryNotes.push(summaryNote);
        });
        let updatedAssessmentHistoryStatuses = {
            ...memberAssessmentHistory.MemberAssessmentStatuses,
        };

        data.IsCarePlanReviewed = isReviewed;
        data.CarePlanReviewedOn = isReviewed ? engagementDraft.CarePlanReviewedOn : null;
        if (!isUpdate) {
            updatedAssessmentHistoryStatuses.CarePlanStatus = assessmentStatusEnum.IN_PROGRESS;
        }
        if (markComplete && isUpdate) {
            updatedAssessmentHistoryStatuses.CarePlanStatus = assessmentStatusEnum.COMPLETED;
        }
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            MemberAssessmentStatuses: updatedAssessmentHistoryStatuses,
            EngagementDraft: data,
        };
        dispatch(saveEngagementDraft(updatedMemberAssessmentHistory, true));
    };

    const handleOnClickSave = () => {
        setShowSaveAlert(true);
    };

    return (
        <>
            <Grid container direction="column" spacing={2} className="content-panel">
                <>
                    <Grid>
                        <CarePlan isAssessment={true} />
                    </Grid>
                    <Grid justifyContent="flex-end" className="content-footer">
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 1 }}
                            disabled={isBusy}
                            onClick={() => dispatch(gotoPreviousMenu())}
                            startIcon={<ArrowBackIosIcon />}
                        >
                            Previous
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isBusy}
                            endIcon={isBusy ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                            onClick={handleOnClickSave}
                        >
                            Next
                        </Button>
                    </Grid>
                </>
            </Grid>
            {isUpdate ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Integrated Care Plan"
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleOnConfirmToNext(true)}
                    onSkip={() => handleOnConfirmToNext()}
                    showBottomCenter={true}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isBusy}
                    message="I confirm and acknowledge that Integrated Care Plan is completed."
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Integrated Care Plan"
                    cancelText="No"
                    okText="Yes, Proceed"
                    onConfirm={() => handleOnConfirmToNext()}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isBusy}
                    showBottomCenter={true}
                    message="Are you sure to proceed without completing Integrated Care Plan?"
                />
            )}
            <Snackbar
                open={error?.Show}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => dispatch(resetCarePlanError())}
            >
                <Alert onClose={() => dispatch(resetCarePlanError())} severity="error" sx={{ width: '100%' }}>
                    {error?.Message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CarePlans;
